import React from 'react'
import {
  Alert,
  Button,
  Card,
  Form,
  InputNumber,
  Modal,
  Space,
  Table,
  Tag,
  Typography
} from 'antd'
import { CopyOutlined, DeleteOutlined, PlusOutlined } from '@ant-design/icons'
import dayjs from 'dayjs'
import { logisticsSettingsApi } from 'api/logisticsSettings'
import {
  ILogisticsSettings
} from 'models/logisticsSettings'
import { getRandomString } from 'shared/utils/getRandomString'
import { showMessage } from 'shared/utils/showMessage'
import { showServerError } from 'shared/utils/showServerError'
import styles from './Logistics.module.scss'
import type { FormInstance, FormListFieldData } from 'antd/es/form'
import type { ColumnsType } from 'antd/es/table'

export type TDeliveryPolicyFormValues = Pick<
  ILogisticsSettings,
  | 'minimum_item_price_per_kg'
  | 'free_delivery_max_cost'
  | 'platform_commission_subsidy_limit_percent'
  | 'delivery_subsidy_rules'
>

interface IDeliveryPolicyFormProps {
  settings: ILogisticsSettings
  onSaved: (settings: ILogisticsSettings) => void
}

interface IPolicyRulesTableProps {
  form: FormInstance<TDeliveryPolicyFormValues>
  fields: FormListFieldData[]
  remove: (index: number | number[]) => void
}

export const getPolicyValues = (
  settings: ILogisticsSettings
): TDeliveryPolicyFormValues => ({
  minimum_item_price_per_kg: settings.minimum_item_price_per_kg,
  free_delivery_max_cost: settings.free_delivery_max_cost,
  platform_commission_subsidy_limit_percent:
    settings.platform_commission_subsidy_limit_percent,
  delivery_subsidy_rules: settings.delivery_subsidy_rules.map((rule) => ({
    key: rule.key,
    minimum_order_sum: rule.minimum_order_sum,
    weight_from_kg: rule.weight_from_kg,
    weight_to_kg: rule.weight_to_kg,
    commission_compensation_percent: rule.commission_compensation_percent
  }))
})

export const serializePolicy = (values: TDeliveryPolicyFormValues) =>
  JSON.stringify(values)

const PolicyRulesTable: React.FC<IPolicyRulesTableProps> = ({
  form,
  fields,
  remove
}) => {
  const rules =
    Form.useWatch('delivery_subsidy_rules', form) ?? []

  const columns: ColumnsType<FormListFieldData> = [
    {
      title: 'Сумма заказа',
      width: 180,
      render: (_, field) => (
        <Form.Item
          className={styles.tableFormItem}
          name={[field.name, 'minimum_order_sum']}
          rules={[{ required: true, message: 'Укажите сумму' }]}
        >
          <InputNumber min={0} addonAfter='₽' className={styles.fullWidth} />
        </Form.Item>
      )
    },
    {
      title: 'Диапазон веса',
      width: 320,
      render: (_, field) => (
        <div className={styles.weightRangeFields}>
          <Form.Item
            className={styles.tableFormItem}
            name={[field.name, 'weight_from_kg']}
            rules={[{ required: true, message: 'Укажите минимальный вес' }]}
          >
            <InputNumber
              min={0}
              addonBefore='от'
              addonAfter='кг'
              aria-label='Вес от'
              className={styles.fullWidth}
            />
          </Form.Item>
          <Form.Item
            className={styles.tableFormItem}
            name={[field.name, 'weight_to_kg']}
            rules={[{ required: true, message: 'Укажите максимальный вес' }]}
          >
            <InputNumber
              min={0}
              addonBefore='до'
              addonAfter='кг'
              aria-label='Вес до'
              className={styles.fullWidth}
            />
          </Form.Item>
        </div>
      )
    },
    {
      title: 'Доля комиссии',
      width: 170,
      render: (_, field) => (
        <Form.Item
          className={styles.tableFormItem}
          name={[field.name, 'commission_compensation_percent']}
          rules={[{ required: true, message: 'Укажите процент' }]}
        >
          <InputNumber
            min={0}
            max={100}
            addonAfter='%'
            aria-label='Доля комиссии правила'
            className={styles.fullWidth}
          />
        </Form.Item>
      )
    },
    {
      title: 'Ключ',
      width: 230,
      render: (_, field) => {
        const ruleKey = rules[field.name]?.key
        return (
          <>
            <Form.Item name={[field.name, 'key']} hidden>
              <input />
            </Form.Item>
            {ruleKey ? (
              <Typography.Text
                code
                copyable={{ icon: <CopyOutlined /> }}
                className={styles.ruleKey}
              >
                {ruleKey}
              </Typography.Text>
            ) : (
              <Typography.Text type='secondary'>
                Создаётся автоматически
              </Typography.Text>
            )}
          </>
        )
      }
    },
    {
      title: '',
      width: 56,
      fixed: 'right',
      render: (_, field) => (
        <Button
          danger
          type='text'
          icon={<DeleteOutlined />}
          aria-label='Удалить правило'
          onClick={() => remove(field.name)}
        />
      )
    }
  ]

  return (
    <Table
      rowKey='key'
      columns={columns}
      dataSource={fields}
      pagination={false}
      scroll={{ x: 900 }}
      locale={{ emptyText: 'Правил пока нет — новая матрица отключена' }}
    />
  )
}

export const DeliveryPolicyForm: React.FC<IDeliveryPolicyFormProps> = ({
  settings,
  onSaved
}) => {
  const [form] = Form.useForm<TDeliveryPolicyFormValues>()
  const [isDirty, setIsDirty] = React.useState(false)
  const [isSaving, setIsSaving] = React.useState(false)
  const baseline = React.useRef(getPolicyValues(settings))

  React.useEffect(() => {
    const values = getPolicyValues(settings)
    baseline.current = values
    form.setFieldsValue(values)
    setIsDirty(false)
  }, [
    form,
    settings.delivery_policy_version,
    settings.delivery_subsidy_rules,
    settings.minimum_item_price_per_kg,
    settings.free_delivery_max_cost,
    settings.platform_commission_subsidy_limit_percent
  ])

  const onValuesChange = (
    _: Partial<TDeliveryPolicyFormValues>,
    allValues: TDeliveryPolicyFormValues
  ) => {
    setIsDirty(
      serializePolicy(allValues) !== serializePolicy(baseline.current)
    )
  }

  const validatePolicy = (values: TDeliveryPolicyFormValues) => {
    const keyIndexes = new Map<string, number>()
    const conditionIndexes = new Map<string, number>()
    const errors: Parameters<typeof form.setFields>[0] = []

    values.delivery_subsidy_rules.forEach((rule, index) => {
      const trimmedKey = rule.key?.trim()
      if (!trimmedKey) {
        errors.push({
          name: ['delivery_subsidy_rules', index, 'key'],
          errors: ['Не удалось создать ключ правила. Добавьте строку заново.']
        })
        return
      }
      const previousKeyIndex = keyIndexes.get(trimmedKey)
      if (previousKeyIndex !== undefined) {
        errors.push({
          name: ['delivery_subsidy_rules', index, 'key'],
          errors: [`Ключ уже используется в строке ${previousKeyIndex + 1}`]
        })
      } else {
        keyIndexes.set(trimmedKey, index)
      }

      const condition = [
        rule.minimum_order_sum,
        rule.weight_from_kg,
        rule.weight_to_kg
      ].join('|')
      const previousConditionIndex = conditionIndexes.get(condition)
      if (previousConditionIndex !== undefined) {
        errors.push({
          name: ['delivery_subsidy_rules', index, 'minimum_order_sum'],
          errors: [
            `Такие сумма заказа и диапазон веса уже указаны в строке ${previousConditionIndex + 1}`
          ]
        })
      } else {
        conditionIndexes.set(condition, index)
      }

      if (rule.weight_from_kg > rule.weight_to_kg) {
        errors.push({
          name: ['delivery_subsidy_rules', index, 'weight_to_kg'],
          errors: ['Вес до не может быть меньше веса от']
        })
      }

      if (
        rule.commission_compensation_percent >
        values.platform_commission_subsidy_limit_percent
      ) {
        errors.push({
          name: [
            'delivery_subsidy_rules',
            index,
            'commission_compensation_percent'
          ],
          errors: [
            'Доля правила не может превышать общий лимит субсидии'
          ]
        })
      }
    })

    if (errors.length) {
      form.setFields(errors)
      form.scrollToField(errors[0].name)
      showMessage(
        'error',
        'Проверьте правила субсидирования',
        String(errors[0].errors?.[0] ?? 'Исправьте отмеченные поля')
      )
      return false
    }
    return true
  }

  const savePolicy = async (values: TDeliveryPolicyFormValues) => {
    try {
      setIsSaving(true)
      const response = await logisticsSettingsApi.updateSettings(values)
      onSaved(response.data)
      showMessage('success', 'Политика доставки сохранена', '')
    } catch (e) {
      showServerError(e)
      throw e
    } finally {
      setIsSaving(false)
    }
  }

  const requestSave = async () => {
    let values: TDeliveryPolicyFormValues
    try {
      values = await form.validateFields()
    } catch {
      return
    }
    if (!validatePolicy(values)) return

    const isEmptyMatrix = values.delivery_subsidy_rules.length === 0
    const hasFreeDeliveryLimit = values.free_delivery_max_cost > 0
    const isDisabling =
      settings.delivery_subsidy_rules.length > 0 && isEmptyMatrix
    Modal.confirm({
      title: isDisabling
        ? 'Отключить матрицу субсидирования?'
        : isEmptyMatrix
          ? 'Сохранить политику без матрицы?'
          : 'Сохранить политику субсидирования?',
      content: isEmptyMatrix
        ? hasFreeDeliveryLimit
          ? 'Матрица будет отключена. Лимит бесплатной доставки продолжит действовать для новых расчётов.'
          : 'Матрица и лимит бесплатной доставки будут отключены: для новых заказов будет использоваться прежняя схема доставки.'
        : `Будет полностью заменён текущий массив правил. Новых правил: ${values.delivery_subsidy_rules.length}.`,
      okText: isDisabling ? 'Отключить матрицу' : 'Сохранить',
      cancelText: 'Отмена',
      okButtonProps: { danger: isDisabling },
      onOk: () => savePolicy(values)
    })
  }

  const resetPolicy = () => {
    form.resetFields()
    form.setFieldsValue(baseline.current)
    setIsDirty(false)
  }

  const rules =
    Form.useWatch('delivery_subsidy_rules', form) ??
    settings.delivery_subsidy_rules

  return (
    <Form
      form={form}
      layout='vertical'
      initialValues={baseline.current}
      onValuesChange={onValuesChange}
      className={styles.policyForm}
    >
      <div className={styles.policyHeader}>
        <div>
          <Space wrap>
            <Typography.Title level={4} className={styles.cardTitle}>
              Политика субсидирования
            </Typography.Title>
            <Tag color={rules.length ? 'green' : 'default'}>
              Матрица {rules.length ? 'включена' : 'отключена'}
            </Tag>
            <Tag>Версия {settings.delivery_policy_version}</Tag>
          </Space>
          <Typography.Text type='secondary' className={styles.updatedAt}>
            Обновлено {dayjs(settings.updatedAt).format('DD.MM.YYYY HH:mm')}
          </Typography.Text>
        </div>
        <Space wrap>
          <Button disabled={!isDirty || isSaving} onClick={resetPolicy}>
            Отменить изменения
          </Button>
          <Button
            type='primary'
            loading={isSaving}
            disabled={!isDirty}
            onClick={requestSave}
          >
            Сохранить политику
          </Button>
        </Space>
      </div>

      <Alert
        type='info'
        showIcon
        message='Сохранённая политика сразу применяется к новым расчётам ПВЗ'
        description='Backend выбирает правило по сумме заказа и весу, затем применяет лимит бесплатной доставки.'
      />

      <Card title='Общие условия' className={styles.policyCard}>
        <div className={styles.policyFields}>
          <Form.Item
            label='Минимальная стоимость килограмма'
            name='minimum_item_price_per_kg'
            rules={[{ required: true, message: 'Укажите стоимость' }]}
          >
            <InputNumber
              min={0}
              addonAfter='₽/кг'
              className={styles.fullWidth}
            />
          </Form.Item>
          <Form.Item
            label='Максимальная доля комиссии на субсидию'
            name='platform_commission_subsidy_limit_percent'
            rules={[{ required: true, message: 'Укажите процент' }]}
          >
            <InputNumber
              min={0}
              addonAfter='%'
              className={styles.fullWidth}
            />
          </Form.Item>
          <Form.Item
            label='Лимит бесплатной доставки'
            name='free_delivery_max_cost'
            rules={[{ required: true, message: 'Укажите лимит' }]}
            extra='Если остаток к оплате покупателем не выше лимита, площадка оплатит доставку полностью. 0 отключает лимит.'
          >
            <InputNumber
              min={0}
              addonAfter='₽'
              className={styles.fullWidth}
            />
          </Form.Item>
        </div>
      </Card>

      <Card className={styles.policyCard}>
        <div className={styles.rulesHeader}>
          <div>
            <Typography.Title level={5} className={styles.cardTitle}>
              Правила субсидирования
            </Typography.Title>
            <Typography.Text type='secondary'>
              При сохранении backend полностью заменит текущий массив правил.
            </Typography.Text>
          </div>
          <Form.List name='delivery_subsidy_rules'>
            {(fields, { add, remove }) => (
              <>
                <Button
                  icon={<PlusOutlined />}
                  onClick={() =>
                    add({
                      key: `rule-${getRandomString()}`,
                      minimum_order_sum: 0,
                      weight_from_kg: 0,
                      weight_to_kg: 0,
                      commission_compensation_percent: 0
                    })
                  }
                >
                  Добавить правило
                </Button>
                <div className={styles.rulesTable}>
                  <PolicyRulesTable
                    form={form}
                    fields={fields}
                    remove={remove}
                  />
                </div>
              </>
            )}
          </Form.List>
        </div>
      </Card>
    </Form>
  )
}
